/**
 * App Component - Root Application Component
 *
 * Provides:
 * - MSAL authentication wrapper
 * - React Router for navigation
 * - Protected routes for authenticated pages
 * - SaaS Onboarding flow integration
 */

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, CircularProgress, Box, Typography, Button } from '@mui/material';
import { InteractionStatus } from '@azure/msal-browser';
import { AuthProvider } from './auth/AuthProvider';
import { UserProvider, useUser } from './contexts/UserContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { WorkflowPage } from './pages/WorkflowPage';
import { OnboardingWizard } from './pages/OnboardingWizard';
import { SubscriptionSuccess } from './pages/SubscriptionSuccess';
import { useAuth } from './hooks/useAuth';
import { setupAuthInterceptors } from './api/client';

// Material-UI theme configuration
const theme = createTheme({
  palette: {
    primary: {
      main: '#461D7C',       // LSU purple
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#FDD023',       // LSU gold
      contrastText: '#3d1500',
    },
  },
});

/**
 * Protected Route Component
 * Redirects to login if not authenticated
 */
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, inProgress } = useAuth();

  // If MSAL is currently handling a redirect or acquiring a token, 
  // show a loading spinner instead of kicking to login.
  if (inProgress !== InteractionStatus.None) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

/**
 * Onboarding Guard Component
 * Ensures users complete onboarding before accessing the app
 */
const OnboardingGuard = ({ children }: { children: React.ReactNode }) => {
  const { status, isLoading, error } = useUser();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Handle Fetch Errors (e.g. 429, 500, Network Error)
  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
        <Typography variant="h5" color="error" gutterBottom>
          Connection Error
        </Typography>
        <Typography variant="body1" align="center" sx={{ mb: 3 }}>
          {error.includes('429')
            ? 'We are experiencing high traffic. Please wait a moment.'
            : 'Unable to load your profile. Please check your connection.'}
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ mb: 2 }}>
          Details: {error}
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry Connection
        </Button>
      </Box>
    );
  }

  // If status is loaded and user is NOT 'Active', force them to onboarding
  // checks if we are NOT already on the /onboarding page to verify infinite loop
  if (status && status.onboarding_status !== 'Active' && !location.pathname.startsWith('/onboarding')) {
    return <Navigate to="/onboarding" replace />;
  }

  // If status is null (and no error/loading), something is wrong, safer to block or show loading
  if (!status) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return <>{children}</>;
};

/**
 * Access Denied Page
 * Shown when Microsoft authentication is successful but backend rejects the user (401)
 */
const AccessDenied = () => {
  const { logout } = useAuth();

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      p: 3,
      textAlign: 'center'
    }}>
      <Typography variant="h4" color="error" gutterBottom>
        Access Denied
      </Typography>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Identity Conflict Detected
      </Typography>
      <Typography variant="body1" sx={{ maxWidth: 500, mb: 4 }}>
        You have successfully signed in with Microsoft, but your account is not currently
        assigned to a role or organization in FaceSheetApp.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={() => window.location.href = '/'}>
          Try Again
        </Button>
        <Button variant="contained" color="primary" onClick={() => logout()}>
          Sign Out & Switch Account
        </Button>
      </Box>
    </Box>
  );
};

/**
 * Auth Interceptor Setup Component
 * Sets up axios interceptors with getAccessToken function
 */
const AuthInterceptorSetup = ({ children }: { children: React.ReactNode }) => {
  const { getAccessToken, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      setupAuthInterceptors(getAccessToken);
    }
  }, [isAuthenticated, getAccessToken]);

  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <SnackbarProvider>
          <AuthInterceptorSetup>
            <UserProvider>
              <BrowserRouter>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/access-denied" element={<AccessDenied />} />

                  {/* Onboarding Flow - Protected by Auth, but NOT by OnboardingGuard */}
                  <Route
                    path="/onboarding"
                    element={
                      <ProtectedRoute>
                        <OnboardingWizard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/subscription-success"
                    element={
                      <ProtectedRoute>
                        <SubscriptionSuccess />
                      </ProtectedRoute>
                    }
                  />

                  {/* Main App - Protected by Auth AND OnboardingGuard */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <OnboardingGuard>
                          <Dashboard />
                        </OnboardingGuard>
                      </ProtectedRoute>
                    }
                  />

                  {/* Workflow Interface */}
                  <Route
                    path="/workflow"
                    element={
                      <ProtectedRoute>
                        <OnboardingGuard>
                          <WorkflowPage />
                        </OnboardingGuard>
                      </ProtectedRoute>
                    }
                  />

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </BrowserRouter>
            </UserProvider>
          </AuthInterceptorSetup>
        </SnackbarProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
